// Angular modules
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  NgModule,
  ErrorHandler,
  SecurityContext
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// 3rd party modules
import { JwtModule } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { RecaptchaModule } from 'ng-recaptcha-2';
import { ImageCropperModule } from 'ngx-image-cropper';

// devextreme-angular imports
import {
  DxBoxModule,
  DxButtonModule,
  DxContextMenuModule,
  DxDrawerModule,
  DxFileUploaderModule,
  DxListModule,
  DxLoadPanelModule,
  DxNumberBoxModule,
  DxPieChartModule,
  DxPopupModule,
  DxProgressBarModule,
  DxResponsiveBoxModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxTextAreaModule,
  DxTextBoxModule,
  DxToolbarModule,
  DxTreeViewModule,
  DxValidationGroupModule,
  DxValidatorModule,
  DxPopoverModule,
  DxDataGridModule,
  DxRadioGroupModule,
  DxCheckBoxModule,
  DxSwitchModule
} from 'devextreme-angular';
import { DevToolsModule } from './dev-tools/dev-tools.component';
import dxDataGrid from 'devextreme/ui/data_grid';
import dxTreeList from 'devextreme/ui/tree_list';
import dxPieChart from 'devextreme/viz/pie_chart';
import dxDropDownButton from 'devextreme/ui/drop_down_button';
import dxScrollable from 'devextreme/ui/scroll_view/ui.scrollable';
import dxScrollView from 'devextreme/ui/scroll_view';
import dxTextBox from 'devextreme/ui/text_box';
import dxTextArea from 'devextreme/ui/text_area';
import dxButton from 'devextreme/ui/button';

// Modules & Components imports
import { CCCommonModule } from '@therealmanage/ciranet-ui-common';
import { AppRoutingModule } from './app-routing.module';
import { CiraButton, CiraModal, CiraToastContainer } from '@ciranet/ui';

// Pipe imports
import { CCCurrencyPipe } from './_pipes/cc-currency.pipe';

// Components imports
import { AppComponent } from './app.component';
import { CalendarHelperService } from './community-module/_services/data-services/calendar-helper.service';
import { PropertyModule } from './property-module/property.module';
import { BrandingImageComponent } from './shared-components/branding-image/branding-image.component';
import { CiraChatComponent } from './shared-components/cira-chat/cira-chat.component';
import { ContactUsComponent } from './shared-components/contact-us/contact-us.component';
import { HeaderComponent } from './shared-components/header/header.component';
import { SelectedPropertyComponent } from './shared-components/selected-property/selected-property.component';
import { SideNavOuterToolbarComponent } from './shared-components/side-nav-outer-toolbar/side-nav-outer-toolbar.component';
import { SideNavigationMenuComponent } from './shared-components/side-navigation-menu/side-navigation-menu.component';
import { UserPanelComponent } from './shared-components/user-panel/user-panel.component';
import { ChangePasswordComponent } from './user-components/change-password/change-password.component';
import { CommonUserAuthenticationLayoutComponent } from './user-components/common-user-authentication-layout/common-user-authentication-layout.component';
import { EditProfileComponent } from './user-components/edit-profile/edit-profile.component';
import { HomeComponent } from './user-components/home/home.component';
import { ImpersonateComponent } from './user-components/impersonate/impersonate.component';
import { LoginComponent } from './user-components/login/login.component';
import { PreferencesComponent } from './user-components/preferences/preferences.component';
import { QuickPaymentComponent } from './user-components/quick-payment/quick-payment.component';
import { ForgotPasswordComponent } from './user-components/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './user-components/reset-password/reset-password.component';
import { RetrieveUsernameOrValidateEmailComponent } from './user-components/retrieve-username-or-validate-email/retrieve-username-or-validate-email.component';
import { SignupComponent } from './user-components/signup/signup.component';
import { RegisterAccountComponent } from './user-components/register-account/register-account.component';
import { RegisterDetailsComponent } from './user-components/register-details/register-details.component';
import { ValidateUserComponent } from './user-components/validate-user/validate-user.component';
import { WelcomeMessageComponent } from './user-components/welcome-message/welcome-message.component';
import { BoardAnnouncementsComponent } from './user-components/board-announcements/board-announcements.component';
import { PortalAnnouncementComponent } from './shared-components/portal-announcement/portal-announcement.component';
import { UserDashboardComponent } from './shared-components/user-dashboard/user-dashboard.component';
import { UserDashboardItemComponent } from './shared-components/user-dashboard-item/user-dashboard-item.component';
import { ResendValidationEmailComponent } from './user-components/resend-validation-email/resend-validation-email.component';
import { SignupAccountNameComponent } from './user-components/signup-account-name/signup-account-name.component';
import { SignupEmailOptionsComponent } from './user-components/signup-email-options/signup-email-options.component';
import { SignupTextOptionsComponent } from './user-components/signup-text-options/signup-text-options.component';
import { UserRegistrationComponent } from './user-components/user-registration/user-registration.component';
import { GoogleCallbackComponent } from './user-components/google-callback/google-callback.component';
import { AppIconComponent } from './shared-components/icon/icon.component';
import { PropertyBannerComponent } from './shared-components/property-banner/property-banner.component';
import { RealAssistComponent } from './shared-components/real-assist/real-assist.component';
import { InactivityWarningModalComponent } from './shared-components/inactivity-warning-modal/inactivity-warning-modal.component';
import { SurveyPopupComponent } from './shared-components/survey-popup/survey-popup.component';
import { MyCommunityTileComponent } from './shared-components/my-community-tile/my-community-tile.component';
import { MeetingHubWidgetComponent } from './shared-components/meeting-hub-widget/meeting-hub-widget.component';
import { ResidentImageEditorComponent } from './shared-components/image-editor/image-editor.component';

// Data imports
import { StorageKeyConstants } from './_constants/storage-key-constants';
import { CredentialsInterceptor } from './_interceptors/credentials-interceptor';
import { DatadogErrorInterceptor } from './_interceptors/datadog-error-interceptor';
import { TransientErrorRetryInterceptor } from './_interceptors/transient-error-retry.interceptor';
import { RPErrorsHandler } from './_interceptors/rp-errors-handler';
import { UriCachingInterceptor } from './_interceptors/uri-caching-interceptor';

// Services imports
import { AppInfoService } from './_services/app-info.service';
import { AuthService } from './_services/auth.service';
import { EnvironmentConfigService } from './_services/environment-config.service';
import { DatadogService } from './_services/datadog.service';
import { ClientErrorService } from './_services/shared-services/client-error.service';
import { DeviceService } from './_services/shared-services/device.service';
import { DomParserService } from './_services/shared-services/dom-parser.service';
import { FileReaderService } from './_services/shared-services/file-reader.service';
import { RPCacheRegistrationService } from './_services/shared-services/rp-cache-registration-service';
import { ScreenService } from './_services/shared-services/screen.service';
import { ServerErrorService } from './_services/shared-services/server-error.service';
import { UserBackgroundService } from './_services/shared-services/user-background.service';

export function ccTokenGetter() {
  return localStorage.getItem(StorageKeyConstants.apiTokenName);
}

export function initializeDatadog(datadogService: DatadogService) {
  return () => datadogService.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    CommonUserAuthenticationLayoutComponent,
    WelcomeMessageComponent,
    RetrieveUsernameOrValidateEmailComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    SignupComponent,
    RegisterAccountComponent,
    RegisterDetailsComponent,
    QuickPaymentComponent,
    HomeComponent,
    SideNavOuterToolbarComponent,
    ContactUsComponent,
    ChangePasswordComponent,
    SideNavigationMenuComponent,
    PreferencesComponent,
    HeaderComponent,
    EditProfileComponent,
    ImpersonateComponent,
    ValidateUserComponent,
    CiraChatComponent,
    BrandingImageComponent,
    SelectedPropertyComponent,
    UserPanelComponent,
    BoardAnnouncementsComponent,
    PortalAnnouncementComponent,
    UserDashboardComponent,
    UserDashboardItemComponent,
    ResendValidationEmailComponent,
    SignupAccountNameComponent,
    SignupEmailOptionsComponent,
    SignupTextOptionsComponent,
    UserRegistrationComponent,
    GoogleCallbackComponent,
    PropertyBannerComponent,
    InactivityWarningModalComponent,
    SurveyPopupComponent,
    MyCommunityTileComponent,
    MeetingHubWidgetComponent,
    CCCurrencyPipe,
    ResidentImageEditorComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    AppRoutingModule,
    PropertyModule,
    CCCommonModule,
    DxBoxModule,
    DxButtonModule,
    DxContextMenuModule,
    DxDrawerModule,
    DxFileUploaderModule,
    DxListModule,
    DxLoadPanelModule,
    DxNumberBoxModule,
    DxPieChartModule,
    DxPopupModule,
    DxProgressBarModule,
    DxResponsiveBoxModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxTreeViewModule,
    DxValidationGroupModule,
    DxValidatorModule,
    DxPopoverModule,
    DxDataGridModule,
    DxRadioGroupModule,
    DxCheckBoxModule,
    DxSwitchModule,
    DevToolsModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: ccTokenGetter,
        allowedDomains: [
          'localhost:8080',
          'localhost:50011', // identity-api
          'localhost:50051', // resident-api
          'localhost:50041', // management-api
          'identity-api.local.ciranet.com',
          'identity-api.dev.ciranet.com',
          'identity-api.demo.ciranet.com',
          'identity-api.qa.ciranet.com',
          'identity-api.ciranet.com',
          'resident-api.local.ciranet.com',
          'resident-api.dev.ciranet.com',
          'resident-api.demo.ciranet.com',
          'resident-api.qa.ciranet.com',
          'resident-api.ciranet.com',
          'management-api.local.ciranet.com',
          'management-api.dev.ciranet.com',
          'management-api.demo.ciranet.com',
          'management-api.qa.ciranet.com',
          'management-api.ciranet.com'
        ]
      }
    }),
    RecaptchaModule,
    ImageCropperModule,
    AppIconComponent,
    RealAssistComponent,
    CiraToastContainer,
    CiraButton,
    CiraModal
  ],
  providers: [
    CookieService,
    AuthService,
    EnvironmentConfigService,
    DatadogService,
    ClientErrorService,
    DeviceDetectorService,
    DeviceService,
    RPCacheRegistrationService,
    ServerErrorService,
    ScreenService,
    AppInfoService,
    UserBackgroundService,
    DomParserService,
    FileReaderService,
    CalendarHelperService,
    // HTTP_INTERCEPTORS — order matters. Interceptors run outer-to-inner
    // in registration order, so the last entry is closest to the backend
    // (and the first to see errors bubbling back up). DatadogErrorInterceptor
    // must stay OUTER of TransientErrorRetryInterceptor — otherwise retried
    // transient failures are reported to RUM once per attempt instead of
    // only on the final outcome. Same rule applies to any future retry /
    // refresh / silent-reauth interceptor added to this list.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UriCachingInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CredentialsInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DatadogErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TransientErrorRetryInterceptor,
      multi: true
    },
    { provide: ErrorHandler, useClass: RPErrorsHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: (envConfig: EnvironmentConfigService) => () =>
        envConfig.initialize(),
      deps: [EnvironmentConfigService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeDatadog,
      deps: [DatadogService, EnvironmentConfigService],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
  exports: [CCCurrencyPipe]
})
export class AppModule {
  constructor(private readonly sanitizer: DomSanitizer) {
    const scrollbarOptions = {
      useNative: false,
      showScrollbar: 'always'
    };
    dxPieChart.defaultOptions({
      options: {
        loadingIndicator: { text: '' }
      }
    });
    dxDropDownButton.defaultOptions({
      options: {
        dropDownOptions: { width: 'auto' }
      }
    });
    dxDataGrid.defaultOptions({
      options: {
        useLegacyColumnButtonTemplate: true
      }
    });
    dxTreeList.defaultOptions({
      options: {
        useLegacyColumnButtonTemplate: true
      }
    });
    // tslint:disable-next-line:deprecation
    dxScrollable.defaultOptions({
      options: scrollbarOptions
    });
    dxScrollView.defaultOptions({
      options: scrollbarOptions
    });
    dxTextBox.defaultOptions({
      options: {
        onFocusOut: this.textValueChanged.bind(this),
        onEnterKey: this.textValueChanged.bind(this),
        inputAttr: { autocomplete: 'off' }
      }
    });
    dxTextArea.defaultOptions({
      options: {
        onFocusOut: this.textValueChanged.bind(this),
        onEnterKey: this.textValueChanged.bind(this),
        inputAttr: { autocomplete: 'off' }
      }
    });
    dxButton.defaultOptions({
      options: {
        onClick: this.onButtonClick.bind(this)
      } as any
    });
    dxScrollView.defaultOptions({
      options: {
        onInitialized: this.onScrollViewInitialized.bind(this)
      }
    });
  }

  textValueChanged(event): void {
    const opt = event.component.option('elementAttr') || { sanitize: false };
    const value = event.component.option('value');
    if (typeof value === 'string') {
      let plainText = value;
      if (!!opt && !!opt.sanitize) {
        plainText = this.sanitizer.sanitize(SecurityContext.HTML, value);
        plainText = this.sanitizer.sanitize(SecurityContext.URL, plainText);
        plainText = this.getSafeText(plainText);
      }
      if (value.trim() !== value || plainText !== value) {
        event.component.option('value', plainText.trim());
      }
    }
  }

  onButtonClick(event: any): void {
    const classList = event.element.className.split(' ');
    const ccDummyButtonIndex = classList.findIndex(
      (d) => d.toLowerCase() === 'cc-dummy-button'.toLowerCase()
    );
    if (ccDummyButtonIndex === -1) {
      event.component.option('disabled', true);
      // ANG-2700: 100ms was not giving best result to avoid multiple API calls, like in case of Save button. Hence increased delay from 100ms to 1000ms
      setTimeout(() => {
        event.component.option('disabled', false);
      }, 1000);
    }
  }

  getSafeText(plain: string): string {
    return plain
      .replace(/&#10;/gi, '\r')
      .replace(/&#34;/gi, '\n')
      .replace('unsafe:', '');
  }

  onScrollViewInitialized(e: any): void {
    // DevExtreme adds a CSS class that causes content shifting on initialization.
    // Using a timeout here ensures the component recalculates properly,
    // forcing an update and scrolling to top to fix the shift.
    // This fix is scoped only to V3 portals where the issue has been observed.
    setTimeout(() => {
      e.component.scrollTo(0);
      e.component.update(); // Force update to recalculate
    }, 100);
  }
}
